import React, { useMemo } from "react";
import TableHeaderCategories from "./components/TableHeaderCategories";
import { TableLoading } from "./components/TableLoading";
import ContentCard from "../misc/ContentCard";
import TableGlobalSearch from "./components/TableGlobalSearch";
import TableGlobalFilter from "./components/TableGlobalFilter";
import TableProvider from "./tableContext/TableProvider";
import {
  TableActions,
  TableData,
  TableFilterDataItem,
  TableHeaderCategoryConfig,
} from "./tableContext/TableContext";
import TablePagination from "./components/TablePagination";
import TableHeader from "./components/TableHeader";
import TableBody from "./components/TableBody";
import useTableConfig from "./hooks/useTableConfig";
import { useSearchParams } from "react-router-dom";
import Text from "../../styleguide/Text";
import useTableCtx from "./tableContext/useTableCtx";
import useTableColumnSizing from "./hooks/useTableColumnSizing";
import TableColumns from "./components/TableColumns";

export type TableItemId = string | number;

type Props = {
  className?: string;
  tableHeaderData: TableHeaderCategoryConfig[];
  tableItemsData: TableData[];
  globalFilter?: TableFilterDataItem[];
  withGlobalSearch?:
    | boolean
    | {
        placeholder?: string;
        searchDelay?: number;
      };
  withGlobalFilter?: boolean;
  borderedRow?: boolean;
  ActionButtonsComponent?: any;
  CustomHeaderComponent?: any;
  tableTitle?: string;
  showSearchForCategories?: boolean;
  withTablePagination?: boolean;
  withRouteParams?: boolean;
  handleTableActions?: (value: TableActions) => void;
  handleUnmount?: () => void;
  tableLoading?: boolean;
  totalItems?: number;
  emptyTableLabel?: string;
};

const TableWidget: React.FC<Props> = (props) => {
  const [searchRouteParams] = useSearchParams();

  const initialTablePagination = useMemo(() => {
    const page = searchRouteParams.get("page");
    const rowsPerPage = searchRouteParams.get("rowsPerPage");
    if (!page && !rowsPerPage) return undefined;
    return {
      page: page ? parseInt(page) : 1,
      rowsPerPage: rowsPerPage ? parseInt(rowsPerPage) : 25,
    };
  }, [searchRouteParams]);

  const filterKeys = useMemo(
    () =>
      new Set(
        props.tableHeaderData
          .filter((header) => header.withFilter)
          .map((header) => header.value),
      ),
    [props.tableHeaderData],
  );
  const initialTableFilter = useMemo(() => {
    const filter: Record<string, string> = {};
    searchRouteParams.forEach((value, key) => {
      if (filterKeys.has(key)) filter[key] = value;
    });
    return Object.keys(filter).length ? filter : undefined;
  }, [searchRouteParams, filterKeys]);

  const initialTableSearch = useMemo(() => {
    const search: Record<string, string> = {};
    searchRouteParams.forEach((value, key) => {
      if (key !== "page" && key !== "rowsPerPage" && !filterKeys.has(key)) {
        search[key] = value;
      }
    });
    return Object.keys(search).length > 0 ? search : undefined;
  }, [searchRouteParams, filterKeys]);

  return (
    <TableProvider
      initialTableSearch={initialTableSearch}
      initialTableFilter={initialTableFilter}
      initialTablePagination={initialTablePagination}>
      <TableComponent {...props} />
    </TableProvider>
  );
};

const TableComponent: React.FC<Props> = ({
  globalFilter,
  tableHeaderData,
  tableItemsData,
  className,
  withGlobalSearch,
  withGlobalFilter,
  borderedRow = true,
  ActionButtonsComponent,
  CustomHeaderComponent,
  tableTitle,
  showSearchForCategories = true,
  withTablePagination = true,
  withRouteParams = true,
  handleTableActions,
  handleUnmount,
  tableLoading,
  totalItems,
  emptyTableLabel = "Tabelul este gol",
}) => {
  const { tableData, tableHeader } = useTableCtx()!;
  const { containerRef, widths, width, fillerWidth } = useTableColumnSizing(
    tableHeader,
    !!ActionButtonsComponent,
    showSearchForCategories,
  );
  useTableConfig({
    handleTableActions,
    handleUnmount,
    globalFilter,
    tableHeaderData,
    tableItemsData,
    withRouteParams,
  });

  return (
    <>
      <ContentCard
        className="min-w-0"
        cardBodyClassName={`relative w-full min-w-0 max-w-full ${
          className ?? ""
        }`}
        cardBodyStyle={{ padding: 0, margin: 0, overflow: "visible" }}
        cardHeaderClassName="border-0 !pt-0 pb-4 pl-1 text-left"
        cardTitleStyle={{ marginBottom: 0 }}
        cardTitle={tableTitle}>
        <div
          ref={containerRef}
          className="w-full min-w-0 max-w-full"
          aria-busy={!!tableLoading}>
          {(withGlobalSearch || withGlobalFilter) && (
            <div className="flex flex-col sm:flex-row">
              {withGlobalSearch && (
                <TableGlobalSearch withGlobalSearch={withGlobalSearch} />
              )}
              {withGlobalFilter && <TableGlobalFilter />}
            </div>
          )}
          <TableHeader CustomHeaderComponent={CustomHeaderComponent} />
          <div
            className="w-full min-w-0 max-w-full overflow-x-auto"
            data-table-scroll>
            <table
              data-table-layout
              aria-label={tableTitle || "Tabel"}
              style={{ width }}
              className={`min-w-full table-fixed border-collapse align-middle [&_td]:px-3 [&_tbody_td]:py-4 [&_th]:px-3 ${
                borderedRow
                  ? "[&_tbody_tr]:border-b [&_tbody_tr]:border-[#f5f8fa]"
                  : ""
              }`}>
              <TableColumns widths={widths} fillerWidth={fillerWidth} />
              <TableHeaderCategories
                showActionButtons={!!ActionButtonsComponent}
                showSearchForCategories={showSearchForCategories}
                withFiller={fillerWidth > 0}
              />
              <TableBody ActionButtonsComponent={ActionButtonsComponent} />
            </table>
          </div>
          {tableData.length === 0 && (
            <div
              role="status"
              className="flex w-full items-center justify-center py-12 text-center">
              <Text
                variant="body1"
                className="!text-2xl font-semibold text-slate-800">
                {emptyTableLabel}
              </Text>
            </div>
          )}
        </div>
        {withTablePagination && <TablePagination totalItems={totalItems} />}
        {tableLoading && <TableLoading />}
      </ContentCard>
    </>
  );
};

export default TableWidget;
